import { useState } from 'react';
import axios from 'axios';
import {
  ArrowRight, CheckCircle2, Bot, Search, Phone, Mail, AlertCircle,
  Sparkles, Zap, Target, FileSearch, TrendingUp, Award, Users, Clock,
  MessageSquare, Brain, Compass, ChevronDown, X, Trophy, LineChart, Layers,
} from 'lucide-react';
import SEO from '@/components/SEO';
import { Reveal } from '@/lib/useReveal';
import { CONTACT_INFO } from '@/lib/content';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AI_PLATFORMS = [
  { name: 'ChatGPT',    tag: 'OpenAI',       icon: MessageSquare },
  { name: 'Gemini',     tag: 'Google',       icon: Sparkles },
  { name: 'Perplexity', tag: 'perplexity.ai', icon: Compass },
  { name: 'Claude',     tag: 'Anthropic',    icon: Brain },
  { name: 'Copilot',    tag: 'Microsoft',    icon: Bot },
  { name: 'Grok',       tag: 'xAI',          icon: Zap },
];

const AI_STATS = [
  { v: '47%',  l: 'of high-intent B2C searches now start in an AI chat' },
  { v: '68%',  l: 'of AI answers cite fewer than five sources per query' },
  { v: '3.4x', l: 'more likely to book when a brand is cited as an answer' },
  { v: '<24h', l: 'is how fast entity gaps compound as AI models retrain' },
];

const METHODOLOGY = [
  { n: '01', icon: Target,    title: 'We map your buyer prompts',      body: 'We build a prompt library from the exact questions your target customers ask AI models before they buy - not generic keyword lists.' },
  { n: '02', icon: FileSearch, title: 'We test across six AI models',  body: 'ChatGPT, Gemini, Perplexity, Claude, Copilot and Grok - each run at multiple temperatures to spot answer volatility.' },
  { n: '03', icon: LineChart, title: 'We measure your citation share', body: 'Every prompt scored for citations, mentions, and answer position - benchmarked against your top three competitors.' },
  { n: '04', icon: Layers,    title: 'We audit your entity signals',   body: 'Schema.org markup, entity coverage, knowledge-graph presence, review platforms, and the citation surfaces AI models actually crawl.' },
  { n: '05', icon: TrendingUp, title: 'You get a 30-day action plan',   body: 'Prioritized fixes ranked by impact and effort - the exact ones we would run internally if this were our own account.' },
];

const REPORT_SECTIONS = [
  'Executive summary and citation share score',
  'Prompt-by-prompt visibility matrix (yours vs. top 3 competitors)',
  'Entity coverage audit and schema opportunities',
  'Top 5 pages to publish or update - with brief and outline',
  'Priority quick wins (24-hour, 7-day, 30-day)',
  'AI model volatility notes (answers that shift between runs)',
];

const COMPARISON = [
  { label: 'Ranking signal',       trad: 'Blue-link rankings (10 slots per page)',     ai: 'Cited as an answer (1-3 sources per query)' },
  { label: 'Optimization target',  trad: 'Keywords, backlinks, on-page',                ai: 'Entities, sources, structured data, freshness' },
  { label: 'Update cadence',       trad: 'Crawled by Googlebot within days',            ai: 'Models retrain quarterly - freshness lag matters' },
  { label: 'Winner-takes-most',    trad: 'Long tail = many winners',                    ai: 'Small pool of cited sources per query' },
  { label: 'CTR / discovery',      trad: 'Users click through to your site',            ai: 'Zero-click answers - brand mention IS the win' },
];

const FAQ = [
  { q: 'What exactly is AI SEO (or AEO)?',
    a: "AI SEO - sometimes called AEO (Answer Engine Optimization) or GEO (Generative Engine Optimization) - is the practice of optimizing your brand, content, and entity signals so that AI chat models like ChatGPT, Gemini, Perplexity, and Claude cite you as an authoritative source when answering questions in your category. It replaces classic 'blue-link' SEO for zero-click, conversational search." },
  { q: 'Do I still need traditional SEO?',
    a: "Yes. AI models pull heavily from indexed web content, structured data, and high-authority sources - all of which are direct outputs of a healthy SEO program. Think of AI SEO as an additional optimization layer on top of a solid technical + content SEO foundation, not a replacement for it." },
  { q: "How is your analysis different from a tool like Perplexity's own audit?",
    a: "Automated tools show you data points. We give you a strategist's read - what to fix first, which content to write, which entity signals matter most for your category, and how your top three competitors are winning. It is human insight backed by real prompt data from all six major AI models." },
  { q: 'How long does the free analysis take?',
    a: 'You will receive your full PDF report and a 15-minute walkthrough call invitation within 48 hours of submitting the form. No calls required to receive the report - it lands in your inbox regardless.' },
  { q: 'Is this really free? What is the catch?',
    a: 'It is genuinely free with no obligation. If you like the report and want us to run the fixes, we can talk about a partnership. If not, the report is yours to run yourself - we would rather you leave with something valuable than nothing at all.' },
  { q: 'What kinds of businesses is this best for?',
    a: 'Any business with a service or product that people research before buying - from local trades to B2B SaaS. We have run AI SEO audits for pool builders, HVAC contractors, healthcare practices, e-commerce brands, and enterprise software companies.' },
  { q: 'How often should we redo this analysis?',
    a: 'AI models retrain every 4-12 weeks, so we recommend a fresh audit every quarter. Citation share and prompt winners can shift significantly with each model update.' },
  { q: 'Do you also do the implementation work?',
    a: "Yes. Brand Partners has run SEO for 19 years and 1,000+ websites. If your report reveals gaps you want us to close, we can execute - schema builds, entity-optimized content, review generation, and technical SEO all in-house." },
];

export default function FreeAISeoAnalysisPage() {
  const [form, setForm] = useState({ name:'', email:'', website:'', industry:'', phone:'' });
  const [status, setStatus] = useState({ state:'idle', message:'' });
  const [openFaq, setOpenFaq] = useState(0);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.website) {
      setStatus({ state:'error', message:'Please fill in your name, email, and website URL.' });
      return;
    }
    setStatus({ state:'loading', message:'' });
    try {
      const res = await axios.post(`${API}/contact`, {
        name: form.name, email: form.email, phone: form.phone, company: form.website,
        service: 'Free AI SEO Analysis', message: `FREE AI SEO ANALYSIS REQUEST\n\nWebsite: ${form.website}\nIndustry: ${form.industry || 'n/a'}`,
      });
      setStatus({ state:'success', message: res.data?.message || "Got it - your AI SEO analysis will arrive within 48 hours." });
      setForm({ name:'', email:'', website:'', industry:'', phone:'' });
      // Google Ads conversion - "SEO Form Submission"
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', { send_to: 'AW-330749360/RdahCMmb7NccELCr250B' });
      }
    } catch {
      setStatus({ state:'error', message:'Something went wrong. Please email hello@bp.agency directly.' });
    }
  };

  const scrollToForm = () => {
    document.getElementById('ai-seo-form-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <main data-testid="free-ai-seo-page" className="bg-white">
      <SEO
        title="Free AI SEO Analysis - Get Found in ChatGPT, Gemini & Perplexity"
        description="Free AI SEO analysis from Brand Partners. Discover where your brand stands in ChatGPT, Gemini, Perplexity, Claude, Copilot & Grok - and how to become the cited answer."
        path="/free-ai-seo-analysis"
      />

      {/* HERO + FORM */}
      <section className="relative pt-12 lg:pt-20 pb-16 bg-radial-blue overflow-hidden">
        <div className="absolute inset-0 bg-dot-grid opacity-40 pointer-events-none"/>
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 relative grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-7">
            <Reveal as="div" className="inline-flex items-center gap-2 bg-bp-teal/10 border border-bp-teal/30 rounded-full px-3 py-1.5">
              <Bot size={14} className="text-bp-teal"/>
              <span className="text-[12px] font-bold uppercase tracking-wider text-bp-teal">AI Search Era · Be the Answer</span>
            </Reveal>
            <Reveal as="h1" delay={80} className="h-display mt-5 text-[44px] sm:text-[64px] lg:text-[78px]">
              Free <span className="grad-text">AI SEO analysis.</span>
            </Reveal>
            <Reveal as="p" delay={140} className="mt-6 text-[17px] text-bp-slate leading-relaxed max-w-xl">
              ChatGPT, Gemini, Perplexity and Claude now answer nearly half of high-intent queries -
              and your competitors are getting cited, not you. We&rsquo;ll show you exactly where you
              stand and what to fix. No commitment, no strings, 48-hour turnaround.
            </Reveal>
            <Reveal as="ul" delay={180} className="mt-7 space-y-3">
              {[
                'Citation share across ChatGPT, Gemini, Perplexity, Claude, Copilot & Grok',
                'Top 20 prompts your buyers are asking - and who wins them',
                'Entity-optimization gaps and schema opportunities',
                'A prioritized 30-day action plan you can run yourself',
              ].map((b) => (
                <li key={b} className="flex items-start gap-3 text-[15.5px] text-bp-slate"><CheckCircle2 size={20} className="text-bp-teal flex-shrink-0 mt-0.5"/>{b}</li>
              ))}
            </Reveal>
            <Reveal as="div" delay={220} className="mt-8 flex flex-wrap items-center gap-6 text-[13px] text-bp-slate-soft">
              <span className="inline-flex items-center gap-2"><Clock size={14}/> 48-hour turnaround</span>
              <span className="inline-flex items-center gap-2"><Award size={14}/> 19 years of SEO experience</span>
              <span className="inline-flex items-center gap-2"><Users size={14}/> 1,000+ sites analyzed</span>
            </Reveal>
          </div>

          <Reveal as="div" delay={150} id="ai-seo-form-anchor" className="lg:col-span-5 scroll-mt-24">
            <form onSubmit={submit} className="rounded-3xl bg-white border border-bp-slate-200 shadow-2xl p-7 lg:p-8" data-testid="free-ai-seo-form">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-bp-teal-50 text-bp-teal flex items-center justify-center"><Search size={20}/></div>
                <div>
                  <div className="font-display font-bold text-bp-ink">Request your free AI SEO analysis</div>
                  <div className="text-[12.5px] text-bp-slate-soft">Delivered within 48 hours</div>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label htmlFor="a-name" className="bp-label">Your name *</label>
                  <input id="a-name" required type="text" className="bp-field" value={form.name} onChange={update('name')} placeholder="Jane Smith"/>
                </div>
                <div>
                  <label htmlFor="a-email" className="bp-label">Email *</label>
                  <input id="a-email" required type="email" className="bp-field" value={form.email} onChange={update('email')} placeholder="jane@company.com"/>
                </div>
                <div>
                  <label htmlFor="a-website" className="bp-label">Website URL *</label>
                  <input id="a-website" required type="text" className="bp-field" value={form.website} onChange={update('website')} placeholder="yourbusiness.com"/>
                </div>
                <div>
                  <label htmlFor="a-industry" className="bp-label">Industry / category</label>
                  <input id="a-industry" type="text" className="bp-field" value={form.industry} onChange={update('industry')} placeholder="e.g. Pool builder, dental practice, B2B SaaS"/>
                </div>
                <div>
                  <label htmlFor="a-phone" className="bp-label">Phone</label>
                  <input id="a-phone" type="tel" className="bp-field" value={form.phone} onChange={update('phone')} placeholder="(555) 123-4567"/>
                </div>
              </div>
              <button type="submit" disabled={status.state === 'loading'} className="btn-primary w-full mt-6 disabled:opacity-60" data-testid="submit-ai-seo">
                {status.state === 'loading' ? 'Sending…' : <>Get My Free AI SEO Analysis <ArrowRight size={16}/></>}
              </button>
              {status.state === 'success' && (
                <div className="mt-4 flex items-start gap-3 p-3 rounded-xl bg-bp-teal-50 border border-bp-teal/30">
                  <CheckCircle2 size={18} className="text-bp-teal mt-0.5 flex-shrink-0"/>
                  <div className="text-[13.5px] text-bp-slate">{status.message}</div>
                </div>
              )}
              {status.state === 'error' && (
                <div className="mt-4 flex items-start gap-3 p-3 rounded-xl bg-red-50 border border-red-200">
                  <AlertCircle size={18} className="text-red-500 mt-0.5 flex-shrink-0"/>
                  <div className="text-[13.5px] text-bp-ink">{status.message}</div>
                </div>
              )}
              <div className="mt-4 text-[11.5px] text-bp-slate-soft text-center">
                We respect your inbox. No spam. Just your analysis.
              </div>
            </form>
          </Reveal>
        </div>
      </section>

      {/* THE AI SEARCH SHIFT - STATS */}
      <section className="py-16 lg:py-20 border-b border-bp-slate-100" data-testid="ai-stats">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="kicker justify-center"><span className="dot"/>The AI search shift</div>
            <h2 className="h-display mt-3 text-[34px] sm:text-[48px]">Search has quietly become <span className="grad-text">conversational.</span></h2>
            <p className="mt-5 text-[16px] text-bp-slate max-w-xl mx-auto">
              The old 10-blue-links game is fading. In its place: AI chat models that pick one - maybe three - sources to represent your entire category.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {AI_STATS.map((s, i) => (
              <Reveal key={s.l} delay={i * 60} className="text-center">
                <div className="font-display font-black text-[42px] sm:text-[54px] leading-none grad-text">{s.v}</div>
                <div className="mt-3 text-[13.5px] text-bp-slate leading-snug">{s.l}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* AI PLATFORMS WE ANALYZE */}
      <section className="py-16 lg:py-20 bg-bp-slate-50" data-testid="ai-platforms">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="kicker justify-center"><span className="dot"/>Coverage</div>
            <h2 className="h-display mt-3 text-[34px] sm:text-[48px]">We test <span className="grad-text">every model</span> your buyers use.</h2>
            <p className="mt-5 text-[16px] text-bp-slate">One AI model is a data point. Six AI models is a truth.</p>
          </div>
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {AI_PLATFORMS.map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal key={p.name} delay={i * 50} className="card-soft p-6 text-center hover:shadow-xl transition-shadow">
                  <div className="w-12 h-12 rounded-xl bg-bp-blue-50 text-bp-blue flex items-center justify-center mx-auto">
                    <Icon size={22}/>
                  </div>
                  <div className="font-display font-bold text-[17px] text-bp-ink mt-4">{p.name}</div>
                  <div className="text-[11.5px] uppercase tracking-wider text-bp-slate-soft mt-1 font-semibold">{p.tag}</div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* METHODOLOGY */}
      <section className="py-20 lg:py-24" data-testid="methodology">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-4">
              <div className="lg:sticky lg:top-28">
                <div className="kicker"><span className="dot"/>The methodology</div>
                <h2 className="h-display mt-3 text-[34px] sm:text-[48px]">A five-step audit built by real <span className="grad-text">SEO strategists.</span></h2>
                <p className="mt-5 text-[16px] text-bp-slate leading-relaxed">
                  This is not a scraper report or a template PDF. Every audit is run manually by a Brand Partners strategist and reviewed by our team before delivery.
                </p>
                <button type="button" onClick={scrollToForm} className="btn-primary mt-8" data-testid="method-cta">
                  Request Yours <ArrowRight size={15}/>
                </button>
              </div>
            </div>
            <div className="lg:col-span-8 space-y-5">
              {METHODOLOGY.map((step, i) => {
                const Icon = step.icon;
                return (
                  <Reveal key={step.n} delay={i * 60} className="card-soft p-6 lg:p-7 flex gap-5 items-start">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-2xl bg-bp-teal-50 text-bp-teal flex items-center justify-center">
                        <Icon size={22}/>
                      </div>
                      <div className="font-display font-extrabold text-[13px] text-bp-slate-soft mt-2 text-center tracking-widest">{step.n}</div>
                    </div>
                    <div>
                      <h3 className="font-display font-bold text-[19px] text-bp-ink">{step.title}</h3>
                      <p className="mt-2 text-[15px] text-bp-slate leading-relaxed">{step.body}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* WHAT'S INSIDE THE REPORT */}
      <section className="py-20 lg:py-24 bg-gradient-to-br from-bp-blue via-[#0a5fd1] to-bp-teal text-white overflow-hidden relative" data-testid="report-preview">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, #fff 1px, transparent 1px), radial-gradient(circle at 80% 60%, #fff 1px, transparent 1px)', backgroundSize: '40px 40px, 60px 60px' }}/>
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 relative grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/30 rounded-full px-3 py-1.5">
              <FileSearch size={14} />
              <span className="text-[12px] font-bold uppercase tracking-wider">Inside your report</span>
            </div>
            <h2 className="h-display text-white mt-5 text-[34px] sm:text-[48px]">A 20-page PDF, delivered to your inbox in <span className="text-[#8DD9CB]">48 hours.</span></h2>
            <p className="mt-5 text-[16.5px] text-white/85 leading-relaxed max-w-lg">
              Every section is written for someone who has to explain the findings to a leadership team - clear, prioritized, and actionable.
            </p>
            <ul className="mt-8 space-y-3">
              {REPORT_SECTIONS.map((r) => (
                <li key={r} className="flex items-start gap-3 text-[15px]"><CheckCircle2 size={20} className="text-[#8DD9CB] flex-shrink-0 mt-0.5"/><span>{r}</span></li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6">
            {/* Mock report card */}
            <div className="rounded-3xl bg-white text-bp-ink shadow-2xl p-6 lg:p-8 border border-white/20 rotate-[-1deg] hover:rotate-0 transition-transform duration-500">
              <div className="flex items-center justify-between border-b border-bp-slate-100 pb-4 mb-5">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-bp-blue text-white flex items-center justify-center font-display font-black text-[12px]">BP</div>
                  <div>
                    <div className="text-[13px] font-bold text-bp-ink">AI SEO Analysis</div>
                    <div className="text-[10.5px] text-bp-slate-soft uppercase tracking-wider">Report · Q3 2026</div>
                  </div>
                </div>
                <div className="text-[10.5px] text-bp-slate-soft">yourbusiness.com</div>
              </div>
              <div className="text-[10.5px] uppercase tracking-wider text-bp-slate-soft font-bold">Citation share vs. competitors</div>
              <div className="mt-4 space-y-3">
                {[
                  { name: 'yourbusiness.com', pct: 22, color: 'bg-bp-blue' },
                  { name: 'competitor-a.com',  pct: 47, color: 'bg-red-400' },
                  { name: 'competitor-b.com',  pct: 34, color: 'bg-amber-400' },
                  { name: 'competitor-c.com',  pct: 18, color: 'bg-bp-slate-300' },
                ].map((row) => (
                  <div key={row.name}>
                    <div className="flex items-center justify-between text-[12px]">
                      <span className="font-semibold text-bp-ink truncate max-w-[60%]">{row.name}</span>
                      <span className="text-bp-slate-soft font-mono">{row.pct}%</span>
                    </div>
                    <div className="mt-1 h-2 rounded-full bg-bp-slate-100 overflow-hidden">
                      <div className={`h-full rounded-full ${row.color}`} style={{ width: `${row.pct}%` }}/>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-5 border-t border-bp-slate-100 grid grid-cols-3 gap-3 text-center">
                <div>
                  <div className="text-[10.5px] uppercase tracking-wider text-bp-slate-soft font-bold">Prompts</div>
                  <div className="font-display font-black text-[22px] text-bp-blue mt-1">20</div>
                </div>
                <div>
                  <div className="text-[10.5px] uppercase tracking-wider text-bp-slate-soft font-bold">Models</div>
                  <div className="font-display font-black text-[22px] text-bp-teal mt-1">6</div>
                </div>
                <div>
                  <div className="text-[10.5px] uppercase tracking-wider text-bp-slate-soft font-bold">Fixes</div>
                  <div className="font-display font-black text-[22px] text-[#F59E0B] mt-1">32</div>
                </div>
              </div>
              <div className="mt-6 text-[11px] text-bp-slate-soft text-center">Sample data - your report is customized to your business</div>
            </div>
          </div>
        </div>
      </section>

      {/* TRADITIONAL SEO vs AI SEO */}
      <section className="py-20 lg:py-24" data-testid="comparison">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="kicker justify-center"><span className="dot"/>Then vs. now</div>
            <h2 className="h-display mt-3 text-[34px] sm:text-[48px]">Traditional SEO vs. <span className="grad-text">AI SEO.</span></h2>
            <p className="mt-5 text-[16px] text-bp-slate">Both still matter. But the winning playbook has completely changed.</p>
          </div>
          <div className="mt-12 rounded-3xl border border-bp-slate-200 overflow-hidden bg-white shadow-lg">
            <div className="grid grid-cols-1 sm:grid-cols-3 bg-bp-slate-50 border-b border-bp-slate-200">
              <div className="hidden sm:block p-4 lg:p-5 text-[12px] font-bold uppercase tracking-wider text-bp-slate-soft">Dimension</div>
              <div className="p-4 lg:p-5 text-[12px] font-bold uppercase tracking-wider text-bp-slate-soft flex items-center gap-2 border-l border-bp-slate-200 sm:border-l">
                <Search size={14}/> Traditional SEO
              </div>
              <div className="p-4 lg:p-5 text-[12px] font-bold uppercase tracking-wider text-bp-blue flex items-center gap-2 border-l border-bp-slate-200">
                <Bot size={14}/> AI SEO
              </div>
            </div>
            {COMPARISON.map((row) => (
              <div key={row.label} className="grid grid-cols-1 sm:grid-cols-3 border-b border-bp-slate-100 last:border-b-0">
                <div className="p-4 lg:p-5 bg-bp-slate-50 sm:bg-white font-semibold text-[14px] text-bp-ink border-b sm:border-b-0 border-bp-slate-100">{row.label}</div>
                <div className="p-4 lg:p-5 text-[14px] text-bp-slate leading-relaxed sm:border-l border-bp-slate-100">{row.trad}</div>
                <div className="p-4 lg:p-5 text-[14px] text-bp-slate leading-relaxed border-l border-bp-slate-100 bg-bp-blue-50/40">{row.ai}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT YOU'LL GET (existing 3 cards - kept + enhanced) */}
      <section className="py-20 bg-bp-slate-50" data-testid="what-you-get">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8">
          <div className="text-center mb-12">
            <div className="kicker justify-center"><span className="dot"/>What you&rsquo;ll get</div>
            <h2 className="h-display mt-3 text-[34px] sm:text-[48px]">A complete <span className="grad-text">AI visibility report.</span></h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { n: '01', title: 'AI Citation Audit',      body: "Where your brand appears (or doesn't) across ChatGPT, Gemini, Perplexity, Claude, Copilot & Grok." },
              { n: '02', title: 'Prompt Opportunity Map', body: 'The top 20 buyer prompts in your category - and who currently wins them.' },
              { n: '03', title: '30-Day Action Plan',     body: 'Specific, prioritized fixes: schema, entity, content, and citation work.' },
            ].map((s) => (
              <div key={s.n} className="card-soft p-7">
                <div className="font-display font-extrabold text-[44px] leading-none grad-text">{s.n}</div>
                <h3 className="font-display font-bold text-[19px] text-bp-ink mt-5">{s.title}</h3>
                <p className="text-[14.5px] text-bp-slate mt-2.5 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-24" data-testid="faq">
        <div className="max-w-[860px] mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="kicker justify-center"><span className="dot"/>Frequently asked</div>
            <h2 className="h-display mt-3 text-[34px] sm:text-[48px]">Questions we hear <span className="grad-text">every week.</span></h2>
          </div>
          <div className="mt-12 space-y-3">
            {FAQ.map((item, i) => {
              const isOpen = openFaq === i;
              return (
                <div key={item.q} className={`rounded-2xl border transition-all ${isOpen ? 'border-bp-blue bg-bp-blue-50/40 shadow-md' : 'border-bp-slate-200 bg-white hover:border-bp-slate-300'}`}>
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? -1 : i)}
                    aria-expanded={isOpen}
                    data-testid={`faq-toggle-${i}`}
                    className="w-full flex items-start justify-between gap-4 text-left p-5 lg:p-6"
                  >
                    <span className={`font-display font-bold text-[16.5px] lg:text-[17.5px] leading-snug ${isOpen ? 'text-bp-blue' : 'text-bp-ink'}`}>{item.q}</span>
                    <ChevronDown size={20} className={`flex-shrink-0 mt-0.5 transition-transform ${isOpen ? 'rotate-180 text-bp-blue' : 'text-bp-slate-soft'}`}/>
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5 lg:px-6 lg:pb-6 -mt-1">
                      <p className="text-[15px] text-bp-slate leading-relaxed">{item.a}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 lg:py-24 bg-bp-slate-50 border-t border-bp-slate-100" data-testid="final-cta">
        <div className="max-w-[900px] mx-auto px-5 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-bp-teal/10 border border-bp-teal/30 rounded-full px-3 py-1.5">
            <Trophy size={14} className="text-bp-teal"/>
            <span className="text-[12px] font-bold uppercase tracking-wider text-bp-teal">Ready when you are</span>
          </div>
          <h2 className="h-display mt-5 text-[36px] sm:text-[54px]">Find out where you stand in <span className="grad-text">AI search.</span></h2>
          <p className="mt-5 text-[17px] text-bp-slate max-w-xl mx-auto">
            48-hour turnaround. Zero commitment. Your custom report, your keep - even if we never speak again.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <button type="button" onClick={scrollToForm} className="btn-primary" data-testid="final-cta-form">
              <FileSearch size={16}/> Get My Free Analysis
            </button>
            <a href={`tel:${CONTACT_INFO.phoneTel}`} className="btn-secondary" data-testid="final-cta-call">
              <Phone size={15}/> Or Call {CONTACT_INFO.phone}
            </a>
          </div>
          <div className="mt-6 text-[13px] text-bp-slate-soft">
            Prefer email?{' '}
            <a href={`mailto:${CONTACT_INFO.email}`} className="font-semibold text-bp-slate hover:text-bp-blue inline-flex items-center gap-1.5"><Mail size={13}/> {CONTACT_INFO.email}</a>
          </div>
        </div>
      </section>
    </main>
  );
}
